import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../../context/LangContext";
import { translations, pickBilingual } from "../../lib/i18n";
import { TEST_IDS } from "../../constants/testIds";

const initialsOf = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const Goalkeepers = ({ goalkeepers = [] }) => {
  const { lang } = useLang();
  const t = translations[lang].goalkeepers;
  const [filter, setFilter] = useState("all");

  const filters = [
    { k: "all", label: t.filters.all },
    { k: "international", label: t.filters.international },
    { k: "first", label: t.filters.first },
    { k: "developed", label: t.filters.developed },
  ];

  const filtered = useMemo(() => {
    return goalkeepers.filter((g) => {
      if (filter === "all") return true;
      if (filter === "international") return g.is_international;
      if (filter === "first") return g.is_first_division;
      if (filter === "developed") return g.is_developed;
      return true;
    });
  }, [goalkeepers, filter]);

  return (
    <section
      id="goalkeepers"
      data-testid={TEST_IDS.goalkeepers.root}
      className="rp-section bg-[color:var(--rp-bg-2)]"
    >
      <div className="rp-container">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 lg:col-span-6">
            <div className="rp-eyebrow mb-4">04 · {t.eyebrow}</div>
            <h2 className="rp-heading rp-headline">{t.title}</h2>
          </div>
          <div className="col-span-12 lg:col-span-6 flex items-end">
            <p className="rp-lede">{t.intro}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {filters.map((f) => (
            <button
              key={f.k}
              data-testid={TEST_IDS.goalkeepers.filter(f.k)}
              onClick={() => setFilter(f.k)}
              className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase border transition-colors ${
                filter === f.k
                  ? "bg-[color:var(--rp-accent)] border-[color:var(--rp-accent)] text-white"
                  : "border-white/15 text-[color:var(--rp-muted)] hover:text-white hover:border-white/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((gk, i) => (
              <motion.article
                key={gk.id || gk.name}
                data-testid={TEST_IDS.goalkeepers.card(i)}
                layout
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, delay: (i % 8) * 0.03 }}
                className="rp-card p-5 flex flex-col gap-4"
              >
                <div className="rp-avatar">
                  {gk.photo_url ? (
                    <img
                      src={gk.photo_url}
                      alt={gk.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span>{initialsOf(gk.name)}</span>
                  )}
                </div>
                <div>
                  <h3 className="font-display font-bold text-white text-lg leading-tight">
                    {gk.name}
                  </h3>
                  {gk.is_international && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[color:var(--rp-accent)]">
                      <span className="w-1 h-1 bg-[color:var(--rp-accent)]" />
                      International
                    </div>
                  )}
                </div>
                <div className="text-[11px] tracking-wider uppercase text-[color:var(--rp-muted)] space-y-1.5">
                  {gk.current_club && (
                    <div>
                      <span className="text-white/40">{t.currentClub}:</span> <span className="text-white/80">{gk.current_club}</span>
                    </div>
                  )}
                  {gk.national_team && (
                    <div>
                      <span className="text-white/40">{t.nationalTeam}:</span> <span className="text-white/80">{gk.national_team}</span>
                    </div>
                  )}
                </div>
                {pickBilingual(gk.description, lang) && (
                  <p className="text-[13px] text-[color:var(--rp-muted)] leading-relaxed">
                    {pickBilingual(gk.description, lang)}
                  </p>
                )}
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        {filtered.length === 0 && (
          <div className="text-center text-[color:var(--rp-muted)] py-16 text-sm">{t.empty}</div>
        )}
      </div>
    </section>
  );
};

export default Goalkeepers;
